import { CalendarIcon } from '@/assets/icons'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useSocket } from '@context/SocketContext'
import { convertDateRangeToPayload, useEvents, useTrip, useTripDayInfos, useUpdateTripDates } from '@hooks'
import { isConflict } from '@lib/apiError'
import { TripHelper } from '@lib/TripHelper'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import PageLoader from '@components/PageLoader'
import Board from './Board/Board'
import EditTripDatesModal from './EditTripDatesModal/EditTripDatesModal'
import ImportStatusBanner from './ImportStatusBanner'
import ShareItineraryModal from './ShareItineraryModal/ShareItineraryModal'
import {
    ConnectionStatusContainer,
    ConnectionStatusDot,
    EmptyState,
    EmptyStateButton,
    EmptyStateContainer,
    EmptyStateIcon,
    EmptyStateText,
    ErrorContainer,
    ItineraryPageContainer,
    LoadingContainer,
} from './ItineraryPage.styles'
import { Itinerary } from './types'
import { generateItinerary } from './utils'

const ItineraryPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const { data: tripDayInfosData } = useTripDayInfos(tripId)
	const [itinerary, setItinerary] = useState<Itinerary | null>(null)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isShareModalOpen, setIsShareModalOpen] = useState(false)
	const { isConnected, joinTrip, leaveTrip } = useSocket()

	const updateTripDates = useUpdateTripDates(tripId || '')

	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? endDate.toISOString().split('T')[0] : ''
			})()
		: ''

	const handleSaveTripDates = (startDate: string, endDate: string) => {
		if (trip && tripId) {
			const payload = convertDateRangeToPayload({ startDate, endDate })
			updateTripDates.mutate(payload, {
				onSuccess: () => {
					toast.success('Trip dates updated successfully!')
				},
				onError: (error) => {
					if (isConflict(error)) {
						toast.error(
							'Cannot shorten trip dates. You have events scheduled on days that would be removed. Please delete or move those events first.',
							{ duration: 10000 }
						)
					} else {
						toast.error('Failed to update trip dates. Please try again.')
					}
				},
			})
		}
	}

	// Join trip room when viewing itinerary and connected
	useEffect(() => {
		if (tripId && isConnected) {
			joinTrip(tripId)

			// Leave trip room on unmount
			return () => {
				leaveTrip(tripId)
			}
		}
	}, [tripId, isConnected, joinTrip, leaveTrip])

	useEffect(() => {
		if (trip) {
			setItinerary(generateItinerary(trip, eventsData?.events, tripDayInfosData?.tripDayInfo))
		}
	}, [trip, eventsData, tripDayInfosData])

	// Set breadcrumbs
	useBreadcrumbs(
		trip
			? [
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Itinerary' },
				]
			: []
	)

	if (tripLoading || eventsLoading) {
		return <LoadingContainer><PageLoader label="Loading trip details…" /></LoadingContainer>
	}

	if (tripError || eventsError) {
		return (
			<ErrorContainer>Error loading trip: {tripError?.message || eventsError?.message}</ErrorContainer>
		)
	}

	// Show trip not found
	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	return (
		<ItineraryPageContainer>
			{import.meta.env.DEV && (
				<ConnectionStatusContainer $isConnected={isConnected}>
					<ConnectionStatusDot $isConnected={isConnected} />
					{isConnected ? 'Connected to real-time updates' : 'Disconnected - changes will not sync'}
				</ConnectionStatusContainer>
			)}

			{tripId && <ImportStatusBanner tripId={tripId} />}

			{!itinerary && trip ? (
				<EmptyStateContainer>
					<EmptyState>
						<EmptyStateIcon>
							<CalendarIcon sx={{ fontSize: 128 }} />
							</EmptyStateIcon>
							<EmptyStateText>Set your trip start date and end date to start planning your itinerary</EmptyStateText>
							<EmptyStateButton onClick={() => setIsEditModalOpen(true)}>Set Trip Dates</EmptyStateButton>
							{tripId && (
								<EmptyStateButton onClick={() => navigate(`/trips/${tripId}/import`)}>
									Import Itinerary
								</EmptyStateButton>
							)}
						</EmptyState>
					</EmptyStateContainer>
			) : (
				itinerary && tripId && trip && (
                    <Board
                        itinerary={itinerary}
                        tripId={tripId}
                        tripStartDate={tripStartDate}
                        tripEndDate={tripEndDate}
                        onEditDatesClick={() => setIsEditModalOpen(true)}
                        onShareClick={() => setIsShareModalOpen(true)}
                        onMapClick={() => navigate(`/trips/${tripId}/map`)}
                        onImportClick={() => navigate(`/trips/${tripId}/import`)}
                    />
				)
			)}

		<EditTripDatesModal
			isOpen={isEditModalOpen}
			onClose={() => setIsEditModalOpen(false)}
			onSave={handleSaveTripDates}
			initialStartDate={tripStartDate}
			initialEndDate={tripEndDate}
			isUpdating={updateTripDates.isPending}
		/>

		{trip.code && (
			<ShareItineraryModal
				isOpen={isShareModalOpen}
				onClose={() => setIsShareModalOpen(false)}
				tripCode={trip.code}
				isVisibleToPublic={trip.isVisibleToPublic}
			/>
		)}
		</ItineraryPageContainer>
	)
}

export default ItineraryPage
